import { Checkbox } from '@timelyapp/react';
import Avatar from 'components/Avatar';

import DurationFormat from 'components/DurationFormat';
import DiagnosticResourceId from 'components/dev-tools/DiagnosticResourceId';
import NumberFormat from 'components/NumberFormat';
import { Td, Tr } from 'components/ui-blocks/table';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { batchSelectionActions } from 'routes/forecast/redux/modules/forecast.types';
import type { AssigneeForecastGroup } from 'routes/forecast/utils/forecast';
import type { ThunkDispatch } from 'types/redux';
import ForecastTr from './ForecastTr';
import TableIdenticator from './TableIdenticator';

interface ForecastAssigneeTrProps {
  showProject?: boolean;
  /** How many px to pad the left side to create indentation? */
  indent?: number;
  forecastIndent?: number;
  row: AssigneeForecastGroup;
  onClickForecast: (id: number, mode?: 'edit' | 'view') => void;
  selections: Set<number>;
  canUpdate?: boolean;
  canDelete?: boolean;
  onOpenRow: (id: number | null) => void;
}

export const ForecastAssigneeTr = (props: ForecastAssigneeTrProps) => {
  const dispatch = useDispatch<ThunkDispatch>();

  const { row, showProject, forecastIndent = 65, canUpdate, canDelete, onOpenRow } = props;
  const { assignee, assignee_summary, forecasts } = row;
  const [isOpen, setIsOpen] = useState(false);
  const handleToggleOpen = useCallback(() => {
    const newOpenState = !isOpen;
    setIsOpen(newOpenState);
    if (newOpenState === true) {
      onOpenRow(assignee?.id ?? null);
    }
  }, [isOpen, onOpenRow, assignee?.id]);

  const stopPropagation = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const forecastIds = forecasts
    .filter(f => {
      if (assignee == null) {
        return f.users.length === 0;
      }
      return f.users.map(u => u.id).includes(assignee.id);
    })
    .map(f => f.id);
  const intersection = forecastIds.filter(a => props.selections.has(a));
  const rowChecked = !!intersection.length && intersection.length === forecastIds.length;
  const indeterminate = !!intersection.length && intersection.length < forecastIds.length;

  const toggleSelection = () => {
    dispatch(
      batchSelectionActions.toggleSelection({
        selectionKey: 'forecastIds',
        value: forecastIds,
        batchRemove: rowChecked,
      })
    );
  };

  const rowContext = useMemo(
    () => ({
      type: 'assignee' as const,
      assigneeId: assignee?.id ?? null,
    }),
    [assignee?.id]
  );

  return (
    <>
      <Tr onClick={handleToggleOpen} selected={isOpen} interactive>
        <Td colSpan={5}>
          <TableIdenticator indent={props.indent} isExpanded={isOpen} />
          {canUpdate && (
            <Checkbox
              style={{ marginRight: 15 }}
              onClick={stopPropagation}
              onChange={toggleSelection}
              checked={rowChecked}
              indeterminate={indeterminate}
            />
          )}
          {assignee != null && (
            <Avatar
              shape="circle"
              src={assignee?.avatar.medium_retina}
              name={assignee?.name}
              fallbackToInitials
              className="fs-exclude"
            />
          )}
          <span style={{ marginLeft: 5 }} className="fs-mask">
            <DiagnosticResourceId resource="user" id={assignee?.id} />
            {assignee != null ? assignee?.name : 'Unassigned'} &nbsp;
          </span>
        </Td>
        <Td>
          <div>
            <strong>
              <DurationFormat
                value={assignee_summary?.total_estimated_minutes}
                format="h[h] m[m]"
                trim="both"
                zeroPlaceholder="0m"
              />
            </strong>{' '}
            est
            <small>
              <NumberFormat value={assignee_summary?.total_percent} decimals={1} />% logged
            </small>
          </div>
        </Td>
        <Td>
          <div>
            <strong>{assignee_summary?.task_count}</strong>{' '}
            <small>{assignee_summary?.task_count === 1 ? 'task' : 'tasks'}</small>
          </div>
        </Td>
      </Tr>
      {isOpen &&
        forecasts.map(forecast => (
          <ForecastTr
            key={forecast.id}
            onClickForecast={props.onClickForecast}
            showProject={showProject}
            indent={forecastIndent}
            forecast={forecast}
            selected={props.selections.has(forecast.id)}
            canUpdate={canUpdate}
            canDelete={canDelete}
            context={rowContext}
          />
        ))}
    </>
  );
};

export default ForecastAssigneeTr;
